// PaywallModal — the single frictionless upgrade surface used by every
// monetization gate (Remove Pomela Badge, Export to Code, Custom Domain).
// Slides up from the bottom on mobile; centred card on desktop.
//
// §LG2 — the pretend-payment card overlay is GONE (even in dev). The upgrade CTA
// opens the REAL embedded Pomela Checkout (PomelaCheckoutBody, PayPal-processed):
// PayPal's own buttons render inline, the subscription is created server-side,
// and success is shown only once the SERVER confirms (plan flip / verified
// subscription). No card fields of ours; no success without server proof.
// In local dev the gateway's mock path may upgrade instantly — that arrives as
// an honest `mock-upgraded` result from the server, never a pretend card form.
//
// The "frictionless" mandate is enforced by:
//   • one slide-up sheet, no multi-page funnel (unlike the full Upgrade screen);
//   • per-gate value-prop copy (pw.<gate>.*) so the user sees the EXACT feature
//     they were unlocking;
//   • checkout happens INSIDE the sheet — no redirect for the default path.

import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Sparkles, X, Check, ArrowRight } from 'lucide-preact';
import { useAuth, useToast } from '@vibeshell/hooks';
import { PLANS } from '@vibeshell/shared';
import { PomelaCheckoutBody, type CheckoutDone } from './PomelaCheckout';
import { t } from '../lib/i18n';

export type PaywallGate = 'remove-badge' | 'export-code' | 'custom-domain';

interface PaywallModalProps {
  gate: PaywallGate;
  open: boolean;
  onClose: () => void;
  /** Optional — when supplied, the modal includes "for <App Name>" in the
   *  headline so the upgrade is anchored to the user's current creation. */
  appName?: string;
}

// Per-gate copy resolves through i18n (pw.<gate>.*) at render so both languages
// stay in sync. Only the stable analytics event + key prefix live here.
const GATE_META: Record<PaywallGate, { keyPrefix: string; analyticsEvent: string }> = {
  'remove-badge': { keyPrefix: 'pw.removeBadge', analyticsEvent: 'paywall_remove_badge_opened' },
  'export-code': { keyPrefix: 'pw.exportCode', analyticsEvent: 'paywall_export_opened' },
  'custom-domain': { keyPrefix: 'pw.customDomain', analyticsEvent: 'paywall_domain_opened' }
};

type Stage = 'pitch' | 'checkout' | 'success';

export function PaywallModal({ gate, open, onClose, appName }: PaywallModalProps): JSX.Element | null {
  const { session } = useAuth();
  const toast = useToast();
  const meta = GATE_META[gate];
  const kp = meta.keyPrefix;
  const bullets = [t(`${kp}.b1`), t(`${kp}.b2`), t(`${kp}.b3`), t(`${kp}.b4`)];
  const [stage, setStage] = useState<Stage>('pitch');

  // Esc closes; matches platform-wide expectation for sheets.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset stage when the modal is reopened from the parent.
  useEffect(() => {
    if (!open) setStage('pitch');
  }, [open]);

  // Fire an analytics event the first time the modal opens for this gate
  // so we can measure conversion by trigger source.
  useEffect(() => {
    if (!open) return;
    window.posthog?.capture(meta.analyticsEvent, { signedIn: session.signedIn, plan: session.plan ?? 'free' });
  }, [open, meta.analyticsEvent, session.signedIn, session.plan]);

  if (!open) return null;

  function startCheckout(): void {
    if (!session.signedIn) {
      toast.show(t('pw.toastSignInFirst'), { variant: 'neutral' });
      onClose();
      location.hash = 'signin';
      return;
    }
    setStage('checkout');
  }

  function onCheckoutDone(done: CheckoutDone): void {
    // Server-confirmed outcomes only. `pro-webhook-pending` keeps the honest
    // pending surface rendered by PomelaCheckoutBody itself — no fake success.
    if (done.kind === 'pro-active' || done.kind === 'mock-upgraded') {
      setStage('success');
      // Reload so the rest of the UI flips into Pro state.
      setTimeout(() => {
        window.location.reload();
      }, 1400);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-headline"
      style={overlay}
      onClick={(e) => {
        // Click outside the card closes.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={card} class="animate-vs-slide-up">
        <button type="button" aria-label={t('common.close')} onClick={onClose} style={closeBtn}>
          <X size={16} />
        </button>

        {stage === 'pitch' ? (
          <>
            <div style={eyebrowWrap}>
              <span style={eyebrowBadge}>
                <Sparkles size={11} /> {t('upg.proBadge')}
              </span>
            </div>

            <h2 id="paywall-headline" style={headlineStyle}>
              {t(`${kp}.headline`)}
            </h2>
            {appName ? (
              <p style={appNameLine}>
                {t('pw.for')} <strong>{appName}</strong>
              </p>
            ) : null}

            <p style={bodyStyle}>{t(`${kp}.body`)}</p>

            <ul style={bulletList}>
              {bullets.map((b) => (
                <li key={b} style={bulletItem}>
                  <span style={bulletCheck}><Check size={11} /></span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div style={priceRow}>
              <div style={priceWrap}>
                <span style={priceAmount}>${PLANS.pro.priceUsdPerMonth}</span>
                <span style={priceCadence}>{t('pw.cadence')}</span>
              </div>
            </div>

            <button type="button" onClick={startCheckout} style={primaryCta}>
              {t(`${kp}.cta`)}
              <ArrowRight size={15} />
            </button>

            <button type="button" onClick={onClose} style={secondaryCta}>
              {t('pw.maybeLater')}
            </button>

            <p style={fineprint}>{t('pw.fineprintPitch')}</p>
          </>
        ) : null}

        {stage === 'checkout' ? (
          <>
            <PomelaCheckoutBody mode="pro" onClose={onClose} onDone={onCheckoutDone} />
            <button type="button" onClick={() => setStage('pitch')} style={secondaryCta}>
              {t('pw.back')}
            </button>
          </>
        ) : null}

        {stage === 'success' ? (
          <div style={{ padding: '40px 8px 16px', textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 18px',
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                background: 'var(--gradient-brand)',
                color: '#fff',
                animation: 'vs-scale-in 0.32s cubic-bezier(0.34, 1.34, 0.64, 1) both'
              }}
            >
              <Check size={26} />
            </div>
            <h2 style={{ ...headlineStyle, fontSize: 22 }}>{t('pw.welcome')}</h2>
            <p style={{ ...bodyStyle, margin: '10px 24px 24px' }}>{t('pw.upgradedReload')}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── styles — token-driven so dark mode tracks automatically ─────────────────

const overlay: JSX.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 90,
  display: 'grid',
  placeItems: 'end center',
  padding: '0 0 max(env(safe-area-inset-bottom, 0px), 0px) 0',
  background: 'color-mix(in srgb, #0a0a0c 55%, transparent)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)'
};

const card: JSX.CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 480,
  background: 'var(--bg)',
  color: 'var(--fg)',
  border: '1px solid var(--border)',
  borderRadius: '24px 24px 0 0',
  padding: '22px 22px 28px',
  boxShadow: '0 -24px 64px -12px rgba(0,0,0,0.32), 0 -2px 6px rgba(0,0,0,0.08)'
};

const closeBtn: JSX.CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 14,
  width: 32,
  height: 32,
  borderRadius: 999,
  border: '1px solid var(--border)',
  background: 'var(--bg-subtle)',
  color: 'var(--fg-muted)',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer'
};

const eyebrowWrap: JSX.CSSProperties = { marginBottom: 10 };

const eyebrowBadge: JSX.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '4px 10px',
  borderRadius: 999,
  background: 'var(--gradient-brand)',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.04em'
};

const headlineStyle: JSX.CSSProperties = {
  margin: 0,
  fontSize: 26,
  fontWeight: 700,
  letterSpacing: '-0.025em',
  lineHeight: 1.15
};

const appNameLine: JSX.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 13.5,
  color: 'var(--fg-muted)'
};

const bodyStyle: JSX.CSSProperties = {
  margin: '12px 0 18px',
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--fg-muted)'
};

const bulletList: JSX.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: '0 0 22px',
  display: 'flex',
  flexDirection: 'column',
  gap: 9
};

const bulletItem: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  fontSize: 13.5,
  lineHeight: 1.45,
  color: 'var(--fg)'
};

const bulletCheck: JSX.CSSProperties = {
  flexShrink: 0,
  width: 20,
  height: 20,
  borderRadius: 999,
  background: 'color-mix(in srgb, var(--color-primary) 14%, transparent)',
  color: 'var(--color-primary)',
  display: 'grid',
  placeItems: 'center',
  marginTop: 2
};

const priceRow: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: 14,
  padding: '12px 14px',
  borderRadius: 14,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)'
};

const priceWrap: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6
};

const priceAmount: JSX.CSSProperties = {
  fontSize: 26,
  fontWeight: 700,
  letterSpacing: '-0.025em',
  color: 'var(--fg)'
};

const priceCadence: JSX.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg-muted)'
};

const primaryCta: JSX.CSSProperties = {
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '13px 18px',
  borderRadius: 14,
  border: 'none',
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary)',
  fontSize: 14.5,
  fontWeight: 700,
  letterSpacing: '-0.005em',
  cursor: 'pointer',
  boxShadow: '0 8px 20px -8px rgba(244, 71, 126, 0.55)'
};

const secondaryCta: JSX.CSSProperties = {
  width: '100%',
  padding: '11px 18px',
  marginTop: 8,
  borderRadius: 12,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--fg-muted)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer'
};

const fineprint: JSX.CSSProperties = {
  margin: '16px 0 0',
  fontSize: 11.5,
  lineHeight: 1.45,
  color: 'var(--fg-subtle)',
  textAlign: 'center'
};
